import { apiClient } from '@/api/client'
import {
  companySecurityLedgerBalancePath,
  companySecurityLedgerBalanceSummaryPath,
  companySecurityLedgerTransactionsPath,
  companySecurityLedgerDeductionsPath,
  companySecurityLedgerAdjustmentsPath,
  companySecurityLedgerMonthlyDeductionPath,
  companySecurityLedgerRefundRequestsPath,
  companySecurityLedgerApproveRefundPath,
  companySecurityLedgerRejectRefundPath,
  SECURITY_LEDGER_ME,
} from '@/api/endpoints'
import type { ApiResponseWrapper } from '@/types/api.types'
import type { PaginatedResponse } from '@/types/common.types'
import type { PagedListBaseParams } from '@/hooks/usePagedListParams'
import type {
  SecurityLedgerTransaction,
  SecurityLedgerBalanceResponse,
  SecurityLedgerBalanceSummary,
  CreateDeductionBody,
  CreateAdjustmentBody,
  RequestRefundBody,
  MySecurityLedgerListQuery,
  MySecurityLedgerListResponse,
} from '@/features/security-ledger/types/security-ledger.types'

/** Path segment `employeeId` is the User document `_id` (Mongo ObjectId). */
export async function getSecurityBalance(
  companyId: string,
  employeeId: string,
): Promise<SecurityLedgerBalanceResponse> {
  const { data } = await apiClient.get<ApiResponseWrapper<SecurityLedgerBalanceResponse>>(
    companySecurityLedgerBalancePath(companyId, employeeId),
  )
  if (data?.data && typeof data.data.balance === 'number') return data.data
  return { balance: 0 }
}

export async function getSecurityBalanceSummary(
  companyId: string,
  employeeId: string,
): Promise<SecurityLedgerBalanceSummary> {
  const { data } = await apiClient.get<ApiResponseWrapper<SecurityLedgerBalanceSummary>>(
    companySecurityLedgerBalanceSummaryPath(companyId, employeeId),
  )
  const d = data?.data
  return {
    balance: d?.balance ?? 0,
    totalDeducted: d?.totalDeducted ?? 0,
    totalAdjusted: d?.totalAdjusted ?? 0,
    totalRefunded: d?.totalRefunded ?? 0,
    pendingRefundTotal: d?.pendingRefundTotal ?? 0,
    // Leave unset when the server omits it so the UI defers to the org currency.
    currency: d?.currency ?? '',
  }
}

export interface SecurityLedgerTxListParams extends PagedListBaseParams {
  type?: SecurityLedgerTransaction['type']
  status?: SecurityLedgerTransaction['status']
}

export async function getSecurityTransactions(
  companyId: string,
  employeeId: string,
  params: SecurityLedgerTxListParams,
): Promise<PaginatedResponse<SecurityLedgerTransaction>> {
  const { data } = await apiClient.get<ApiResponseWrapper<PaginatedResponse<SecurityLedgerTransaction>>>(
    companySecurityLedgerTransactionsPath(companyId, employeeId),
    { params },
  )
  return data.data
}

export async function createDeduction(
  companyId: string,
  body: CreateDeductionBody,
): Promise<SecurityLedgerTransaction> {
  const { data } = await apiClient.post<ApiResponseWrapper<SecurityLedgerTransaction>>(
    companySecurityLedgerDeductionsPath(companyId),
    body,
  )
  const doc = data?.data
  if (!doc?._id) throw new Error('Invalid create deduction response')
  return doc
}

export async function createAdjustment(
  companyId: string,
  body: CreateAdjustmentBody,
): Promise<SecurityLedgerTransaction> {
  const { data } = await apiClient.post<ApiResponseWrapper<SecurityLedgerTransaction>>(
    companySecurityLedgerAdjustmentsPath(companyId),
    body,
  )
  const doc = data?.data
  if (!doc?._id) throw new Error('Invalid create adjustment response')
  return doc
}

export async function applyMonthlyDeduction(
  companyId: string,
  employeeId: string,
): Promise<SecurityLedgerTransaction> {
  const { data } = await apiClient.post<ApiResponseWrapper<SecurityLedgerTransaction>>(
    companySecurityLedgerMonthlyDeductionPath(companyId, employeeId),
  )
  const doc = data?.data
  if (!doc?._id) throw new Error('Invalid monthly deduction response')
  return doc
}

export async function requestRefund(
  companyId: string,
  employeeId: string,
  body: RequestRefundBody,
): Promise<SecurityLedgerTransaction> {
  const { data } = await apiClient.post<ApiResponseWrapper<SecurityLedgerTransaction>>(
    companySecurityLedgerRefundRequestsPath(companyId, employeeId),
    body,
  )
  const doc = data?.data
  if (!doc?._id) throw new Error('Invalid refund request response')
  return doc
}

/** txMongoId must be the Mongo document `_id`, not the UUID txId field. */
export async function approveRefund(
  companyId: string,
  txMongoId: string,
): Promise<SecurityLedgerTransaction> {
  const { data } = await apiClient.post<ApiResponseWrapper<SecurityLedgerTransaction>>(
    companySecurityLedgerApproveRefundPath(companyId, txMongoId),
  )
  const doc = data?.data
  if (!doc?._id) throw new Error('Invalid approve refund response')
  return doc
}

/** txMongoId must be the Mongo document `_id`, not the UUID txId field. */
export async function rejectRefund(
  companyId: string,
  txMongoId: string,
  reason?: string,
): Promise<SecurityLedgerTransaction> {
  const { data } = await apiClient.post<ApiResponseWrapper<SecurityLedgerTransaction>>(
    companySecurityLedgerRejectRefundPath(companyId, txMongoId),
    reason ? { reason } : {},
  )
  const doc = data?.data
  if (!doc?._id) throw new Error('Invalid reject refund response')
  return doc
}

/** Cross-company self-service ledger: backend auto-scopes to caller's memberships. */
export async function getMySecurityLedger(
  query?: MySecurityLedgerListQuery,
): Promise<MySecurityLedgerListResponse> {
  const { companyIds, ...rest } = query ?? {}
  const params: Record<string, unknown> = { ...rest }
  if (companyIds && companyIds.length > 0) {
    params.companyIds = companyIds.join(',')
  }
  const { data } = await apiClient.get<ApiResponseWrapper<MySecurityLedgerListResponse>>(
    SECURITY_LEDGER_ME,
    { params },
  )
  const payload = data?.data
  if (payload && Array.isArray(payload.data)) return payload
  return { data: [], pagination: { total: 0, page: 1, limit: query?.limit ?? 10 } }
}
